import * as React from "react";

import type { GridRow } from "./gridRow";

interface UseRowExpansionOptions<TData> {
  data: TData[];
  getRowId: (row: TData) => string;
  /**
   * Row ids to expand on first mount, e.g. when a deep link lands on one row
   * with its detail panel open. Captured once; later changes are ignored — the
   * hook owns expansion state afterward. A seeded id whose row hasn't loaded
   * yet survives an async first load: the prune is skipped while `data` is
   * empty *and the seed is still pending*, so an empty initial fetch can't
   * clear the seed before its row arrives.
   */
  initialExpandedIds?: ReadonlySet<string>;
}

export type ExpandableGridRow<TData> = GridRow<TData> & {
  __detail?: boolean;
  __expanded?: boolean;
};

export interface UseRowExpansionReturn<TData> {
  expandedIds: Set<string>;
  isExpanded: (id: string) => boolean;
  toggle: (id: string) => void;
  /** Original rows interleaved with full-width detail rows for expanded items. */
  displayRows: ExpandableGridRow<TData>[];
}

/**
 * Internal helper for `<DataTable>` that turns the consumer's data array into
 * a sequence of grid rows interleaved with full-width detail rows for any
 * currently-expanded items. Resets expansion when the underlying data
 * changes (sort / page / pageSize).
 */
export function useRowExpansion<TData>(
  options: UseRowExpansionOptions<TData>,
): UseRowExpansionReturn<TData> {
  const { data, getRowId, initialExpandedIds } = options;
  const [expandedIds, setExpandedIds] = React.useState<Set<string>>(
    () => new Set(initialExpandedIds ?? []),
  );
  // True only while a non-empty seed's rows haven't loaded yet; flips false
  // the first time `data` arrives non-empty and never back. The empty-data
  // prune skip gates on this rather than on `data.length === 0` so a no-seed
  // consumer still prunes on an empty refetch — its user-toggled rows collapse
  // when the view empties instead of lingering and auto-re-expanding.
  const seedPendingRef = React.useRef((initialExpandedIds?.size ?? 0) > 0);

  React.useEffect(() => {
    if (data.length === 0) {
      // Empty data while a seed is pending is the async first load: skip the
      // prune so the seed survives until its row arrives. Otherwise the rows
      // are genuinely gone, so fall through and let the prune clear them.
      if (seedPendingRef.current) {
        return;
      }
    } else {
      seedPendingRef.current = false;
    }
    setExpandedIds((prev) => {
      if (prev.size === 0) {
        return prev;
      }
      const visibleIds = new Set(data.map(getRowId));
      const next = new Set<string>();
      prev.forEach((id) => {
        if (visibleIds.has(id)) {
          next.add(id);
        }
      });
      return next.size === prev.size ? prev : next;
    });
  }, [data, getRowId]);

  const isExpanded = React.useCallback(
    (id: string) => expandedIds.has(id),
    [expandedIds],
  );

  const toggle = React.useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const displayRows = React.useMemo<ExpandableGridRow<TData>[]>(() => {
    const rows: ExpandableGridRow<TData>[] = [];
    for (const row of data) {
      const id = getRowId(row);
      const expanded = expandedIds.has(id);
      rows.push({
        ...row,
        __detail: false,
        __expanded: expanded,
      });
      if (expanded) {
        rows.push({
          ...row,
          __detail: true,
          __expanded: true,
        });
      }
    }
    return rows;
  }, [data, getRowId, expandedIds]);

  return { expandedIds, isExpanded, toggle, displayRows };
}
